import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Constants } from "@eagerdog/constants";
import {
  IFetchAdvancedScore,
  IFetchIntermediateScore,
  IFetchNoviceScore,
  IFetchRetrieverScore,
  IScore
} from "@eagerdog/interfaces";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Input from "src/components/Input/Input";
import Checkbox from "src/components/Checkbox/Checkbox";

import "./FetchResults.scss";

interface IProps {
  result: IScore,
  onChange(result: any, judgeApproval: boolean, judgeName: string, judgeNumber: string): void
}

const FetchResults: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const [judgeName, setJudgeName] = useState<string>(props.result.judge_name ?? "");
  const [judgeNumber, setJudgeNumber] = useState<string>(props.result.judge_number ?? "");
  const [judgeApproval, setJudgeApproval] = useState<boolean>(false);

  const [dropdownAnswers, setDropdownAnswers] = useState<IOption[]>([]);
  const [fullAnswersOption, setFullAnswersOption] = useState<IOption>({ value: "Pass", id: "pass" });

  const [other, setOther] = useState<string>();

  const dropdownSize = useMemo(() => ({
    [Constants.dog_class_element_level.novice]: 10,
    [Constants.dog_class_element_level.intermediate]: 13,
    [Constants.dog_class_element_level.advanced]: 19,
    [Constants.dog_class_element_level.retriever]: 22,
  }), []);

  const _options:IOption[] = useMemo(() => ([
    { value: "Pass", id: "pass" },
    { value: "Failed", id: "needs_training" },
    { value: "Excused - Runs off Course", id: "runs_off_course" },
    { value: "Excused - Urinates/Defecates on Course", id: "urinates_defecates" },
    { value: "Excused - Refusal", id: "refusal" },
    { value: "Excused - Does Not Finish in Time", id: "does_not_finish_in_time" },
    { value: "Excused - Other", id: "other" },
    { value: "Disqualified", id: "disqualified" },
    { value: "Absent", id: "absent" }
  ]), []);

  const changeDropdownAnswer = (value: IOption, index: number) => {
    let _answers:IOption[] = [...dropdownAnswers];

    _answers[index] = value;

    let needsTraining = _answers.filter((opt: IOption) => { return (opt.value === "Needs Training"); }).length > 0;
    if (!needsTraining) setFullAnswersOption({ value: "Pass", id: "pass" },)

    setDropdownAnswers(_answers);
  }

  const createAnswerDropdown = (label: string, id: string, index: number, options?: IOption[]) => {
    let _options:IOption[] = [
      { value: "Pass", id: "Pass" },
      { value: "Needs Training", id: "Needs Training" }
    ];

    return <Dropdown label={label} id={id} value={dropdownAnswers[index]} onChange={(e: any, value: IOption) => { changeDropdownAnswer(value, index); setHasChanged(true); }} options={options ? options : _options} placeholder="" />
  }

  const createTitle = (text: string) => {
    return <>
      <div className="fetchTitle">{text}</div>
    </>;
  }

  const fullAnswersChangeDropdown = () => {
    return <Dropdown
      label={"Result"}
      id={"full_answers"}
      value={fullAnswersOption}
      onChange={(e: any, value: IOption) => {
        setFullAnswersOption(value)
        switch (value.id) {
          case "pass": {
            if (props.result.dog_class?.level) {
              setDropdownAnswers(
                Array.from({ length: dropdownSize[props.result.dog_class.level] }, () => ({ value: "Pass", id: "Pass" }))
              );
            }
            break
          }
          case "needs_training": {
            if (props.result.dog_class?.level) {
              setDropdownAnswers(
                Array.from({ length: dropdownSize[props.result.dog_class?.level] }, () => ({ value: "Needs Training", id: "Needs Training" }))
              );
            }
            break
          }
          case "runs_off_course":
          case "urinates_defecates":
          case "refusal":
          case "does_not_finish_in_time":
          case "other":
          case "disqualified":
          case "absent": {
            break
          }
        }
        setHasChanged(true);
      }}
      options={_options}
      placeholder=""
    />
  }

  const getResultType = useCallback(() => {
    if (fullAnswersOption.id === "disqualified") {
      return Constants.score_result_type.disqualified
    }

    if (fullAnswersOption.id === "absent") {
      return Constants.score_result_type.absent
    }

    if (["runs_off_course", "urinates_defecates", "refusal", "does_not_finish_in_time", "other"].includes(fullAnswersOption.id)) {
      return Constants.score_result_type.excused
    }

    let needsTraining = dropdownAnswers.filter((opt: IOption) => { return (opt.value === "Needs Training"); }).length > 0;
    if (needsTraining) {
      return Constants.score_result_type.not_qualified
    }

    return Constants.score_result_type.qualified
  }, [fullAnswersOption.id, dropdownAnswers]);

  const getLevelResultForm = () => {
    if (props.result && props.result.dog_class && fullAnswersOption.id === "needs_training") {
      switch (props.result.dog_class.level) {
        case Constants.dog_class_element_level.novice:
        case Constants.dog_class_element_level.intermediate:
          return <>
            {createAnswerDropdown("Dog under control at the start line", "dog_under_control_at_the_start_line", 0)}
            {createTitle("Handler Throws 3 Singles (Center)")}
            {createAnswerDropdown("Dog Goes to Ball", "dog_goes_to_ball_1", 1)}
            {createAnswerDropdown("Picks Up Ball", "picks_up_ball_1", 2)}
            {createAnswerDropdown("Returns Ball Within 2 Steps", "returns_ball_1", 3)}
            {createTitle("Handler Throws 3 Singles (Left)")}
            {createAnswerDropdown("Dog Goes to Ball", "dog_goes_to_ball_2", 4)}
            {createAnswerDropdown("Picks Up Ball", "picks_up_ball_2", 5)}
            {createAnswerDropdown("Returns Ball Within 2 Steps", "returns_ball_2", 6)}
            {createTitle("Handler Throws 3 Singles (Right)")}
            {createAnswerDropdown("Dog Goes to Ball", "dog_goes_to_ball_3", 7)}
            {createAnswerDropdown("Picks Up Ball", "picks_up_ball_3", 8)}
            {createAnswerDropdown("Returns Ball Within 2 Steps", "returns_ball_3", 9)}
            {props.result.dog_class.level === Constants.dog_class_element_level.intermediate && <>
              {createTitle("Handler Throws 3 Singles (Left or Right)")}
              {createAnswerDropdown("Dog Goes to Ball", "dog_goes_to_ball_3", 10)}
              {createAnswerDropdown("Picks Up Ball", "picks_up_ball_3", 11)}
              {createAnswerDropdown("Returns Ball Within 2 Steps", "returns_ball_3", 12)}
            </>}
          </>;
        case Constants.dog_class_element_level.advanced:
          return <>
            {createAnswerDropdown("Dog under control at the start line", "dog_under_control_at_the_start_line", 0)}
            {createTitle("Handler Throws 2 Singles (Center, Single)")}
            {createAnswerDropdown("Dog Goes to Ball", "dog_goes_to_ball_1", 1)}
            {createAnswerDropdown("Picks Up Ball", "picks_up_ball_1", 2)}
            {createAnswerDropdown("Returns to Handler", "returns_ball_1", 3)}
            {createTitle("Handler Throws 2 Singles (Left or Right, Single)")}
            {createAnswerDropdown("Dog Goes to Ball", "dog_goes_to_ball_2", 4)}
            {createAnswerDropdown("Picks Up Ball", "picks_up_ball_2", 5)}
            {createAnswerDropdown("Returns to Handler", "returns_ball_2", 6)}
            {createTitle("Handler Throws 2 Doubles #1")}
            {createAnswerDropdown("Dog Goes to Ball (Ball #1)", "dog_goes_to_ball_3", 7)}
            {createAnswerDropdown("Picks Up Ball (Ball #1)", "picks_up_ball_3", 8)}
            {createAnswerDropdown("Returns to Handler (Ball #1)", "returns_ball_3", 9)}
            {createAnswerDropdown("Dog Goes to Ball (Ball #2)", "dog_goes_to_ball_4", 10)}
            {createAnswerDropdown("Picks Up Ball (Ball #2)", "picks_up_ball_4", 11)}
            {createAnswerDropdown("Returns to Handler (Ball #2)", "returns_ball_4", 12)}
            {createTitle("Handler Throws 2 Doubles #2")}
            {createAnswerDropdown("Dog Goes to Ball (Ball #1)", "dog_goes_to_ball_5", 13)}
            {createAnswerDropdown("Picks Up Ball (Ball #1)", "picks_up_ball_5", 14)}
            {createAnswerDropdown("Returns to Handler (Ball #1)", "returns_ball_5", 15)}
            {createAnswerDropdown("Dog Goes to Ball (Ball #2)", "dog_goes_to_ball_6", 16)}
            {createAnswerDropdown("Picks Up Ball (Ball #2)", "picks_up_ball_6", 17)}
            {createAnswerDropdown("Returns to Handler (Ball #2)", "returns_ball_6", 18)}
          </>;
        case Constants.dog_class_element_level.retriever:
          return <>
            {createAnswerDropdown("Dog under control at the start line", "dog_under_control_at_the_start_line", 0)}
            {createTitle("Handler Throws 1 Triple (Ball 1, Center)")}
            {createAnswerDropdown("Dog Goes to Ball", "dog_goes_to_ball_1", 1)}
            {createAnswerDropdown("Picks Up Ball", "picks_up_ball_1", 2)}
            {createAnswerDropdown("Returns to Handler", "returns_ball_1", 3)}
            {createTitle("Handler Throws 1 Triple (Ball 2, Left)")}
            {createAnswerDropdown("Dog Goes to Ball", "dog_goes_to_ball_2", 4)}
            {createAnswerDropdown("Picks Up Ball", "picks_up_ball_2", 5)}
            {createAnswerDropdown("Returns to Handler", "returns_ball_2", 6)}
            {createTitle("Handler Throws 1 Triple (Ball 3, Right)")}
            {createAnswerDropdown("Dog Goes to Ball", "dog_goes_to_ball_3", 7)}
            {createAnswerDropdown("Picks Up Ball", "picks_up_ball_3", 8)}
            {createAnswerDropdown("Returns to Handler", "returns_ball_3", 9)}
            {createTitle("Handler Throws 2 Doubles (Double #1)")}
            {createAnswerDropdown("Dog Goes to Ball (Ball #1)", "dog_goes_to_ball_4", 10)}
            {createAnswerDropdown("Picks Up Ball (Ball #1)", "picks_up_ball_4", 11)}
            {createAnswerDropdown("Returns to Handler (Ball #1)", "returns_ball_4", 12)}
            {createAnswerDropdown("Dog Goes to Ball (Ball #2)", "dog_goes_to_ball_5", 13)}
            {createAnswerDropdown("Picks Up Ball (Ball #2)", "picks_up_ball_5", 14)}
            {createAnswerDropdown("Returns to Handler (Ball #2)", "returns_ball_5", 15)}
            {createTitle("Handler Throws 2 Doubles (Double #2)")}
            {createAnswerDropdown("Dog Goes to Ball (Ball #1)", "dog_goes_to_ball_6", 16)}
            {createAnswerDropdown("Picks Up Ball (Ball #1)", "picks_up_ball_6", 17)}
            {createAnswerDropdown("Returns to Handler (Ball #1)", "returns_ball_6", 18)}
            {createAnswerDropdown("Dog Goes to Ball (Ball #2)", "dog_goes_to_ball_7", 19)}
            {createAnswerDropdown("Picks Up Ball (Ball #2)", "picks_up_ball_7", 20)}
            {createAnswerDropdown("Returns to Handler (Ball #2)", "returns_ball_7", 21)}
          </>;
      }
    }
  }

  const checkIfPassed = useCallback(() => {
    let needsTraining = dropdownAnswers.filter((opt: IOption) => { return (opt.value === "Needs Training"); }).length > 0;

    return !(needsTraining || fullAnswersOption.id !== "pass");
  }, [dropdownAnswers, fullAnswersOption]);

  const getOptionValue = (val: boolean | undefined) => {
    if (val) {
      return { value: "Pass", id: "Pass" };
    } else {
      return { value: "Needs Training", id: "Needs Training" };
    }
  };

  const levelConfig = useMemo(() => ({
    novice: (data: IFetchNoviceScore) => [
      getOptionValue(data.dog_under_control_at_the_start_line),
      getOptionValue(data.single_to_center?.dog_goes_to_ball),
      getOptionValue(data.single_to_center?.picks_up_ball),
      getOptionValue(data.single_to_center?.returns_ball),
      getOptionValue(data.single_to_left?.dog_goes_to_ball),
      getOptionValue(data.single_to_left?.picks_up_ball),
      getOptionValue(data.single_to_left?.returns_ball),
      getOptionValue(data.single_to_right?.dog_goes_to_ball),
      getOptionValue(data.single_to_right?.picks_up_ball),
      getOptionValue(data.single_to_right?.returns_ball),
    ],

    intermediate: (data: IFetchIntermediateScore) => [
      getOptionValue(data.dog_under_control_at_the_start_line),
      getOptionValue(data.single_to_center?.dog_goes_to_ball),
      getOptionValue(data.single_to_center?.picks_up_ball),
      getOptionValue(data.single_to_center?.returns_ball),
      getOptionValue(data.single_to_left?.dog_goes_to_ball),
      getOptionValue(data.single_to_left?.picks_up_ball),
      getOptionValue(data.single_to_left?.returns_ball),
      getOptionValue(data.single_to_right?.dog_goes_to_ball),
      getOptionValue(data.single_to_right?.picks_up_ball),
      getOptionValue(data.single_to_right?.returns_ball),
      getOptionValue(data.single_to_left_or_right?.dog_goes_to_ball),
      getOptionValue(data.single_to_left_or_right?.picks_up_ball),
      getOptionValue(data.single_to_left_or_right?.returns_ball),
    ],

    advanced: (data: IFetchAdvancedScore) => [
      getOptionValue(data.dog_under_control_at_the_start_line),
      getOptionValue(data.single_to_center?.dog_goes_to_ball),
      getOptionValue(data.single_to_center?.picks_up_ball),
      getOptionValue(data.single_to_center?.returns_ball),
      getOptionValue(data.single_to_left_or_right?.dog_goes_to_ball),
      getOptionValue(data.single_to_left_or_right?.picks_up_ball),
      getOptionValue(data.single_to_left_or_right?.returns_ball),
      getOptionValue(data.double_1?.[0].dog_goes_to_ball),
      getOptionValue(data.double_1?.[0].picks_up_ball),
      getOptionValue(data.double_1?.[0].returns_ball),
      getOptionValue(data.double_1?.[1].dog_goes_to_ball),
      getOptionValue(data.double_1?.[1].picks_up_ball),
      getOptionValue(data.double_1?.[1].returns_ball),
      getOptionValue(data.double_2?.[0].dog_goes_to_ball),
      getOptionValue(data.double_2?.[0].picks_up_ball),
      getOptionValue(data.double_2?.[0].returns_ball),
      getOptionValue(data.double_2?.[1].dog_goes_to_ball),
      getOptionValue(data.double_2?.[1].picks_up_ball),
      getOptionValue(data.double_2?.[1].returns_ball),
    ],

    retriever: (data: IFetchRetrieverScore) => [
      getOptionValue(data.dog_under_control_at_the_start_line),
      getOptionValue(data.triple?.[0].ball_result?.dog_goes_to_ball),
      getOptionValue(data.triple?.[0].ball_result?.picks_up_ball),
      getOptionValue(data.triple?.[0].ball_result?.returns_ball),
      getOptionValue(data.triple?.[1].ball_result?.dog_goes_to_ball),
      getOptionValue(data.triple?.[1].ball_result?.picks_up_ball),
      getOptionValue(data.triple?.[1].ball_result?.returns_ball),
      getOptionValue(data.triple?.[2].ball_result?.dog_goes_to_ball),
      getOptionValue(data.triple?.[2].ball_result?.picks_up_ball),
      getOptionValue(data.triple?.[2].ball_result?.returns_ball),
      getOptionValue(data.double_1?.[0].dog_goes_to_ball),
      getOptionValue(data.double_1?.[0].picks_up_ball),
      getOptionValue(data.double_1?.[0].returns_ball),
      getOptionValue(data.double_1?.[1].dog_goes_to_ball),
      getOptionValue(data.double_1?.[1].picks_up_ball),
      getOptionValue(data.double_1?.[1].returns_ball),
      getOptionValue(data.double_2?.[0].dog_goes_to_ball),
      getOptionValue(data.double_2?.[0].picks_up_ball),
      getOptionValue(data.double_2?.[0].returns_ball),
      getOptionValue(data.double_2?.[1].dog_goes_to_ball),
      getOptionValue(data.double_2?.[1].picks_up_ball),
      getOptionValue(data.double_2?.[1].returns_ball),
    ],
  }), []);


  const levelsMap = useMemo(() => ({
    [Constants.dog_class_element_level.novice]: {
      fetchKey: "novice",
      configFn: levelConfig["novice"],
    },
    [Constants.dog_class_element_level.intermediate]: {
      fetchKey: "intermediate",
      configFn: levelConfig["intermediate"],
    },
    [Constants.dog_class_element_level.advanced]: {
      fetchKey: "advanced",
      configFn: levelConfig["advanced"],
    },
    [Constants.dog_class_element_level.retriever]: {
      fetchKey: "retriever",
      configFn: levelConfig["retriever"],
    },
  }), [levelConfig]);


  const handleExcused = useCallback((doesNotQualify: any) => {
    if (!doesNotQualify) return false;

    if (doesNotQualify.runs_off_course) {
      setFullAnswersOption({ value: "Excused - Runs off Course", id: "runs_off_course" });
    } else if (doesNotQualify.urinates_on_course) {
      setFullAnswersOption({ value: "Excused - Urinates/Defecates on Course", id: "urinates_defecates" });
    } else if (doesNotQualify.refusal_to_retrieve) {
      setFullAnswersOption({ value: "Excused - Refusal", id: "refusal" });
    } else if (doesNotQualify.does_not_finish_in_time) {
      setFullAnswersOption({ value: "Excused - Does Not Finish in Time", id: "does_not_finish_in_time" });
    } else if (doesNotQualify.other) {
      setFullAnswersOption({ value: "Excused - Other", id: "other" });
      setOther(doesNotQualify.other_description || "");
    } else {
      return false;
    }

    return true;
  }, []);

  const handleLevelResult = useCallback(
    (level: string, fetch: any, resultType?: string) => {
      const { fetchKey, configFn } = levelsMap[level];
      const levelData = fetch?.[fetchKey];
      if (levelData) {
        if (resultType) {
          switch (resultType) {
            case Constants.score_result_type.not_qualified:
              setFullAnswersOption({ value: "Failed", id: "needs_training" });
              setDropdownAnswers(configFn(levelData));
              break;
            case Constants.score_result_type.disqualified:
              setFullAnswersOption({ value: "Disqualified", id: "disqualified" });
              break;
            case Constants.score_result_type.absent:
              setFullAnswersOption({ value: "Absent", id: "absent" });
              break;
            case Constants.score_result_type.excused:
              if (!handleExcused(levelData?.does_not_qualify)) {
                setFullAnswersOption({ value: "Failed", id: "needs_training" });
                setDropdownAnswers(configFn(levelData));
              }
              break;
          }
        } else {
          if (!handleExcused(levelData?.does_not_qualify)) {
            setFullAnswersOption({ value: "Failed", id: "needs_training" });
            setDropdownAnswers(configFn(levelData));
          }
        }
      }
    },
    [levelsMap, handleExcused, setFullAnswersOption, setDropdownAnswers]
  );

  useEffect(() => {
    if (!loaded && props.result?.dog_class) {
      if (props.result.fetch?.passed) {
        setFullAnswersOption({ value: "Pass", id: "pass" });
      } else {
        handleLevelResult(
          props.result.dog_class.level,
          props.result.fetch,
          props.result.fetch?.result_type as string | undefined
        );
      }
      setLoaded(true);
    }
  }, [handleLevelResult, loaded, props.result.dog_class, props.result.fetch]);


  useEffect(() => {
    if (hasChanged) {
      const determineDropdownValue = (val: string) => {
        return val === "Pass";
      }

      let _changed: any = {
        result_type: getResultType(),
        passed: checkIfPassed()
      };

      if (props.result && props.result.dog_class) {
        switch (props.result.dog_class.level) {
          case Constants.dog_class_element_level.novice:
            if (["pass", "needs_training"].includes(fullAnswersOption.id)) {
              _changed.novice = {
                dog_under_control_at_the_start_line: determineDropdownValue(dropdownAnswers[0].value),
                single_to_center: {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[1].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[2].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[3].value)
                },
                single_to_left: {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[4].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[5].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[6].value)
                },
                single_to_right: {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[7].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[8].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[9].value)
                },
                does_not_qualify: {
                  runs_off_course: false,
                  urinates_on_course: false,
                  refusal_to_retrieve: false,
                  does_not_finish_in_time: false,
                  other: false,
                  other_description: undefined,
                },
              }
            }

            if (["runs_off_course", "urinates_on_course", "refusal_to_retrieve", "does_not_finish_in_time", "other"].includes(fullAnswersOption.id)) {
              _changed.novice = {
                dog_under_control_at_the_start_line: false,
                does_not_qualify: {
                  runs_off_course: fullAnswersOption.id === "runs_off_course",
                  urinates_on_course: fullAnswersOption.id === "urinates_on_course",
                  refusal_to_retrieve: fullAnswersOption.id === "refusal_to_retrieve",
                  does_not_finish_in_time: fullAnswersOption.id === "does_not_finish_in_time",
                  other: fullAnswersOption.id === "other",
                  other_description: other,
                },
              }
            }
          break;
          case Constants.dog_class_element_level.intermediate:
            if (["pass", "needs_training"].includes(fullAnswersOption.id)) {
              _changed.intermediate = {
                dog_under_control_at_the_start_line: determineDropdownValue(dropdownAnswers[0].value),
                single_to_center: {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[1].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[2].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[3].value)
                },
                single_to_left: {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[4].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[5].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[6].value)
                },
                single_to_right: {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[7].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[8].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[9].value)
                },
                single_to_left_or_right: {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[10].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[11].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[12].value)
                },
                does_not_qualify: {
                  runs_off_course: false,
                  urinates_on_course: false,
                  refusal_to_retrieve: false,
                  does_not_finish_in_time: false,
                  other: false,
                  other_description: undefined,
                },
              }
            }

            if (["runs_off_course", "urinates_on_course", "refusal_to_retrieve", "does_not_finish_in_time", "other"].includes(fullAnswersOption.id)) {
              _changed.intermediate = {
                dog_under_control_at_the_start_line: false,
                does_not_qualify: {
                  runs_off_course: fullAnswersOption.id === "runs_off_course",
                  urinates_on_course: fullAnswersOption.id === "urinates_on_course",
                  refusal_to_retrieve: fullAnswersOption.id === "refusal_to_retrieve",
                  does_not_finish_in_time: fullAnswersOption.id === "does_not_finish_in_time",
                  other: fullAnswersOption.id === "other",
                  other_description: other,
                },
              }
            }
          break;
          case Constants.dog_class_element_level.advanced:
            if (["pass", "needs_training"].includes(fullAnswersOption.id)) {
              _changed.advanced = {
                dog_under_control_at_the_start_line: determineDropdownValue(dropdownAnswers[0].value),
                single_to_center: {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[1].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[2].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[3].value)
                },
                single_to_left_or_right: {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[4].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[5].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[6].value)
                },
                double_1: [{
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[7].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[8].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[9].value)
                }, {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[10].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[11].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[12].value)
                }],
                double_2: [{
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[13].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[14].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[15].value)
                }, {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[16].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[17].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[18].value)
                }],
                does_not_qualify: {
                  runs_off_course: false,
                  urinates_on_course: false,
                  refusal_to_retrieve: false,
                  does_not_finish_in_time: false,
                  other: false,
                  other_description: undefined,
                },
              }
            }

            if (["runs_off_course", "urinates_on_course", "refusal_to_retrieve", "does_not_finish_in_time", "other"].includes(fullAnswersOption.id)) {
              _changed.advanced = {
                dog_under_control_at_the_start_line: false,
                does_not_qualify: {
                  runs_off_course: fullAnswersOption.id === "runs_off_course",
                  urinates_on_course: fullAnswersOption.id === "urinates_on_course",
                  refusal_to_retrieve: fullAnswersOption.id === "refusal_to_retrieve",
                  does_not_finish_in_time: fullAnswersOption.id === "does_not_finish_in_time",
                  other: fullAnswersOption.id === "other",
                  other_description: other,
                },
              }
            }
          break;
          case Constants.dog_class_element_level.retriever:
            if (["pass", "needs_training"].includes(fullAnswersOption.id)) {
              _changed.retriever = {
                dog_under_control_at_the_start_line: determineDropdownValue(dropdownAnswers[0].value),
                triple: [{
                  ball_direction: "center",
                  ball_result: {
                    dog_goes_to_ball: determineDropdownValue(dropdownAnswers[1].value),
                    picks_up_ball: determineDropdownValue(dropdownAnswers[2].value),
                    returns_ball: determineDropdownValue(dropdownAnswers[3].value)
                  }
                }, {
                  ball_direction: "left",
                  ball_result: {
                    dog_goes_to_ball: determineDropdownValue(dropdownAnswers[4].value),
                    picks_up_ball: determineDropdownValue(dropdownAnswers[5].value),
                    returns_ball: determineDropdownValue(dropdownAnswers[6].value)
                  }
                }, {
                  ball_direction: "right",
                  ball_result: {
                    dog_goes_to_ball: determineDropdownValue(dropdownAnswers[7].value),
                    picks_up_ball: determineDropdownValue(dropdownAnswers[8].value),
                    returns_ball: determineDropdownValue(dropdownAnswers[9].value)
                  }
                }],
                double_1: [{
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[10].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[11].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[12].value)
                }, {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[13].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[14].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[15].value)
                }],
                double_2: [{
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[16].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[17].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[18].value)
                }, {
                  dog_goes_to_ball: determineDropdownValue(dropdownAnswers[19].value),
                  picks_up_ball: determineDropdownValue(dropdownAnswers[20].value),
                  returns_ball: determineDropdownValue(dropdownAnswers[21].value)
                }],
                does_not_qualify: {
                  runs_off_course: false,
                  urinates_on_course: false,
                  refusal_to_retrieve: false,
                  does_not_finish_in_time: false,
                  other: false,
                  other_description: undefined,
                },
              }
            }

            if (["runs_off_course", "urinates_on_course", "refusal_to_retrieve", "does_not_finish_in_time", "other"].includes(fullAnswersOption.id)) {
              _changed.retriever = {
                dog_under_control_at_the_start_line: false,
                does_not_qualify: {
                  runs_off_course: fullAnswersOption.id === "runs_off_course",
                  urinates_on_course: fullAnswersOption.id === "urinates_on_course",
                  refusal_to_retrieve: fullAnswersOption.id === "refusal_to_retrieve",
                  does_not_finish_in_time: fullAnswersOption.id === "does_not_finish_in_time",
                  other: fullAnswersOption.id === "other",
                  other_description: other,
                },
              }
            }
          break;
        } 
      }

      props.onChange(_changed, judgeApproval, judgeName, judgeNumber);

      setHasChanged(false);
    }
  }, [hasChanged, props, checkIfPassed, dropdownAnswers, other, judgeApproval, judgeName, judgeNumber, getResultType, fullAnswersOption.id]);

  return (
    <div className="FetchResults">
      {loaded && <div className="resultWrap">
        {fullAnswersChangeDropdown()}
        {getLevelResultForm()}
        {fullAnswersOption.id === "other" && (
          <div className="other">
            {createTitle("Does Not Qualify")}
            <Input required defaultValue={other} onChange={(e: any) => { setOther(e.target.value); setHasChanged(true); }} type="text" label="Other" placeholder="Enter another reason" />
          </div>
        )}
        {props.result.show_type === Constants.show_type.fetch && <div className="judgeInfo">
          <Input
            label="Judge Name"
            id="judgeName"
            type="text"
            placeholder="What's the judges name?"
            value={judgeName}
            onChange={(e) => { setJudgeName(e.target.value); setHasChanged(true); }}
          />
          <Input
            label="Judge #"
            id="judgeNum"
            type="text"
            defaultValue={judgeNumber}
            onChange={(e) => { setJudgeNumber(e.target.value); setHasChanged(true); }}
          />
        </div>}
        <Checkbox
          id="judge_approval"
          label="These results were entered by the judge and/or approved by the judge for signature"
          value={judgeApproval}
          onChange={(e: any) => {
            setJudgeApproval(!judgeApproval);
            setHasChanged(true);
          }}
        />
      </div>}
    </div>
  );
};

export default FetchResults;